import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Plus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useBusiness } from '../../context/BusinessContext';
import { useBusinessSubscription } from '../../hooks/useBusinessSubscription';
import { useThemeStyles } from '../../theme/useThemeStyles';
import AllStaffCalendar from '../StaffManagement/AllStaffCalendar';
import StaffScheduleGrid from './StaffScheduleGrid';
import AddStaffModal from './AddStaffModal';
import EditStaffModal from './EditStaffModal';

function Schedules() {
  const styles = useThemeStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { selectedBusiness, businessId } = useBusiness();
  const { planKey, loading: subscriptionLoading } = useBusinessSubscription();
  const isFreePlan = subscriptionLoading || planKey === 'supplier_free';
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addStaffModalOpen, setAddStaffModalOpen] = useState(false);
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'calendar'

  const effectiveBusinessId = businessId || selectedBusiness?.business?._id;

  const fetchData = React.useCallback(async () => {
    if (!effectiveBusinessId) return;
    
    setLoading(true);
    setError('');
    try {
      const staffRes = await api.get(`/api/supplier/staff-members?businessId=${effectiveBusinessId}`);
      
      setStaffMembers(staffRes.data.staffMembers || []);
    } catch (err) {
      setError('Unable to load staff members. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [effectiveBusinessId]);

  useEffect(() => {
    fetchData();
    
    // Refresh when page becomes visible (user navigates back)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData]);

  const handleAddStaff = () => {
    setAddStaffModalOpen(true);
  };

  const handleStaffAdded = (newStaffMember) => {
    setStaffMembers(prev => [...prev, newStaffMember]);
    setAddStaffModalOpen(false);
    // Refresh schedules in case one was linked
    fetchData();
  };

  const handleStaffDeleted = (staffMemberId) => {
    setStaffMembers(prev => prev.filter(sm => sm._id !== staffMemberId));
  };

  const handleStaffUpdated = (updatedStaffMember) => {
    setStaffMembers(prev =>
      prev.map(sm => sm._id === updatedStaffMember._id ? updatedStaffMember : sm)
    );
    setEditModalOpen(false);
    // Refresh schedules in case schedule was added/updated
    fetchData();
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center',
        gap: 2
      }}>
        <Box>
          <Typography component="h2" className="pagename" sx={styles.text.pagename}>
            Schedules
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage when your team can work
          </Typography>
        </Box>
        {(!isFreePlan || staffMembers.length === 0) && (
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={handleAddStaff}
            sx={isMobile ? { alignSelf: 'flex-start' } : undefined}
          >
            Add Staff
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}


      {/* Conditional Rendering based on view mode */}
      {viewMode === 'grid' ? (
        <StaffScheduleGrid 
          businessId={effectiveBusinessId}
          staffMembers={staffMembers}
          isFreePlan={isFreePlan}
          onShiftClick={(shift) => {
            // Navigate to individual staff schedule page
            const staffId = shift.teamMember?._id?.toString() || shift.teamMember?.toString();
            navigate(`/supplier/schedules/staff/${staffId}`);
          }}
          onStaffEdit={(staffMember) => {
            setSelectedStaffMember(staffMember);
            setEditModalOpen(true);
          }}
          onViewCalendar={() => setViewMode('calendar')}
        />
      ) : (
        <AllStaffCalendar
          businessId={effectiveBusinessId}
          onBackToGrid={() => setViewMode('grid')}
        />
      )}

      <AddStaffModal
        open={addStaffModalOpen}
        onClose={() => setAddStaffModalOpen(false)}
        onStaffAdded={handleStaffAdded}
        businessId={effectiveBusinessId}
      />

      <EditStaffModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedStaffMember(null);
        }}
        staffMember={selectedStaffMember}
        onStaffUpdated={handleStaffUpdated}
        businessId={effectiveBusinessId}
        onStaffDeleted={handleStaffDeleted}
      />

    </Box>
  );
}

export default Schedules;

